import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { trackEvent } from "@/utils/analytics";
// Import icons individually to improve performance
import { 
  Shield, 
  FileText, 
  BarChart, 
  Globe, 
  BookOpen, 
  Sparkles, 
  ClipboardCheck, 
  Layers 
} from "lucide-react";

export const GridFeatures = () => {
  const { t, i18n } = useTranslation('common');
  const [features, setFeatures] = useState([
    {
      id: "grid-feature-1",
      title: "",
      description: "",
      icon: <FileText size={24} />,
    },
    {
      id: "grid-feature-2",
      title: "",
      description: "",
      icon: <BarChart size={24} />,
    },
    {
      id: "grid-feature-3",
      title: "",
      description: "",
      icon: <Layers size={24} />,
    },
    {
      id: "grid-feature-4",
      title: "",
      description: "",
      icon: <Shield size={24} />,
    },
    {
      id: "grid-feature-5",
      title: "",
      description: "",
      icon: <Sparkles size={24} />,
    },
    {
      id: "grid-feature-6",
      title: "",
      description: "",
      icon: <BookOpen size={24} />,
    },
    {
      id: "grid-feature-7",
      title: "",
      description: "",
      icon: <ClipboardCheck size={24} />,
    },
    {
      id: "grid-feature-8",
      title: "",
      description: "",
      icon: <Globe size={24} />,
    },
  ]);
  
  // Initialize feature translations
  const updateFeatures = () => {
    setFeatures([
      {
        id: "grid-feature-1",
        title: t('features.gridFeatures.items.0.title'),
        description: t('features.gridFeatures.items.0.description'),
        icon: <FileText size={24} />,
      },
      {
        id: "grid-feature-2",
        title: t('features.gridFeatures.items.1.title'),
        description: t('features.gridFeatures.items.1.description'),
        icon: <BarChart size={24} />,
      },
      {
        id: "grid-feature-3",
        title: t('features.gridFeatures.items.2.title'),
        description: t('features.gridFeatures.items.2.description'),
        icon: <Layers size={24} />,
      },
      {
        id: "grid-feature-4",
        title: t('features.gridFeatures.items.3.title'),
        description: t('features.gridFeatures.items.3.description'),
        icon: <Shield size={24} />,
      },
      {
        id: "grid-feature-5",
        title: t('features.gridFeatures.items.4.title'),
        description: t('features.gridFeatures.items.4.description'),
        icon: <Sparkles size={24} />,
      },
      {
        id: "grid-feature-6",
        title: t('features.gridFeatures.items.5.title'),
        description: t('features.gridFeatures.items.5.description'),
        icon: <BookOpen size={24} />,
      },
      {
        id: "grid-feature-7",
        title: t('features.gridFeatures.items.6.title'),
        description: t('features.gridFeatures.items.6.description'),
        icon: <ClipboardCheck size={24} />,
      },
      {
        id: "grid-feature-8",
        title: t('features.gridFeatures.items.7.title'),
        description: t('features.gridFeatures.items.7.description'),
        icon: <Globe size={24} />,
      },
    ]);
  };
  
  // Update features when language changes
  useEffect(() => {
    updateFeatures();
  }, [t, i18n.language]);
  
  // Listen for language change events
  useEffect(() => {1
    const handleLanguageChange = () => {
      updateFeatures();
    };
    
    window.addEventListener('languageChanged', handleLanguageChange);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
    };
  }, [t]);
  
  return (
    <div className="relative z-20 mb-10 rounded-xls">
      {/* Background panel with proper light/dark support */}
      <div className="absolute inset-0 bg-white/90 dark:bg-black/80 backdrop-blur-sm -z-10 rounded-xl shadow-lg border border-neutral-200 dark:border-neutral-800"></div>
      

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 py-10">
        {features.map((feature, index) => (
          <Feature key={feature.id} {...feature} index={index} />
        ))}
      </div>
    </div>
  );
};

const Feature = ({
  title,
  description,
  icon,
  index,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  index: number;
}) => {
  // Track feature click for analytics
  const handleFeatureClick = () => {
    trackEvent('Feature', 'Click', title);
  };

  return (
    <div
      onClick={handleFeatureClick}
      className={cn(
        "flex flex-col py-8 relative group cursor-pointer",
        "border-neutral-200 dark:border-neutral-700 ",
        (index === 0 || index === 4) && "lg:border-l",
        index < 4 && "lg:border-b",
        index % 4 !== 3 && "lg:border-r"
      )}
    >
      {/* Hover effect gradient */}
      <div className="opacity-0 group-hover:opacity-100 transition duration-300 absolute inset-0 h-full w-full bg-gradient-to-r from-transparent via-blue-50/50 dark:via-blue-900/20 to-transparent pointer-events-none" />
      
      {/* Icon with background */}
      <div className="mb-4 relative z-10 px-10">
        <div className="p-2 rounded-lg bg-blue-50/50 dark:bg-blue-900/20 inline-block">
          {icon}
        </div>
      </div>
      
      {/* Title with animated accent */}
      <div className="text-lg font-bold mb-2 relative z-10 px-10">
        <div className="absolute left-0 inset-y-0 h-6 w-1 rounded-tr-full rounded-br-full bg-neutral-300 dark:bg-neutral-700 group-hover:bg-blue-500 transition duration-200" />
        <span className="group-hover:translate-x-2 transition duration-200 inline-block">
          {title}
        </span>
      </div>
      
      {/* Description */}
      <p className="text-sm text-neutral-600 dark:text-neutral-300 max-w-xs mx-auto relative z-10 px-10">
        {description}
      </p>
    </div>
  );
};
